import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Edit, Briefcase } from 'lucide-react';

const EditJobForm = ({ job, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: job?.title || '',
    description: job?.description || '',
    requirements: job?.requirements || '',
    location: job?.location || '',
    salary: job?.salary || '',
    employmentType: job?.employmentType || 'Full-time',
    sport: job?.sport || '',
    status: job?.status || 'open'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSave(job._id, formData);
  };

  return (
    <div className="space-y-5">
      {/* Job Title */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Job Title *</Label>
        <Input
          type="text"
          className="input-premium"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="e.g., Football Coach, Sports Analyst"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Description *</Label>
        <textarea
          className="input-premium min-h-[120px] resize-none"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Describe the role and responsibilities..."
        />
      </div>

      {/* Requirements */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Requirements</Label>
        <textarea
          className="input-premium min-h-[100px] resize-none"
          value={formData.requirements}
          onChange={(e) => handleChange('requirements', e.target.value)}
          placeholder="List the requirements for this position..."
        />
      </div>

      {/* Location & Salary Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Location</Label>
          <Input
            type="text"
            className="input-premium"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="e.g., London, UK"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Salary</Label>
          <Input
            type="text"
            className="input-premium"
            value={formData.salary}
            onChange={(e) => handleChange('salary', e.target.value)}
            placeholder="e.g., £40,000 or Competitive"
          />
        </div>
      </div>

      {/* Employment Type & Sport Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Employment Type</Label>
          <select
            className="input-premium"
            value={formData.employmentType}
            onChange={(e) => handleChange('employmentType', e.target.value)}
          >
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
            <option value="Contract">Contract</option>
            <option value="Freelance">Freelance</option>
            <option value="Internship">Internship</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Sport</Label>
          <Input
            type="text"
            className="input-premium"
            value={formData.sport}
            onChange={(e) => handleChange('sport', e.target.value)}
            placeholder="e.g., Football, Basketball"
          />
        </div>
      </div>

      {/* Status */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Status</Label>
        <select
          className="input-premium"
          value={formData.status}
          onChange={(e) => handleChange('status', e.target.value)}
        >
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-border">
        <Button variant="outline" onClick={() => onClose(false)} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button 
          className="btn-primary w-full sm:w-auto"
          onClick={handleSubmit}
        >
          <Briefcase className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const EditJobModal = ({ 
  isOpen, 
  onClose, 
  job, 
  onSave 
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4 p-0" data-testid="edit-job-modal">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-purple-500/10 to-primary/10 p-6 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <div className="icon-container-green">
                <Edit className="w-5 h-5 text-purple-500" />
              </div>
              Edit Job Posting
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6">
          {job && (
            <EditJobForm 
              key={job._id} 
              job={job} 
              onSave={onSave} 
              onClose={onClose} 
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditJobModal;
